import type { MembershipFreeze } from '@/types';
import { getBaliDateKey, addDaysToBaliKey, diffBaliDays, toBaliDateKey } from '@/lib/date-utils';

/**
 * Returns the freeze period that contains today's date (Bali time), or null if none.
 * Freezes are stored as YYYY-MM-DD date strings, so we compare with today's Bali
 * (Asia/Makassar) YYYY-MM-DD key — independent of the admin's browser timezone.
 * Boundaries are inclusive: a freeze starting or ending today is considered current.
 */
export function getCurrentFreeze(
  freezes: MembershipFreeze[] | undefined | null
): MembershipFreeze | null {
  if (!Array.isArray(freezes) || freezes.length === 0) return null;
  const todayStr = getBaliDateKey(new Date());
  return freezes.find(f => todayStr >= f.start_date && todayStr <= f.end_date) || null;
}

type FreezeDates = { id: string; start_date: string; end_date: string; duration_days: number };

/**
 * The end the membership would have with no freezes at all: the stored
 * end_date already includes every freeze's days, so subtract all of them.
 */
export function getUnfrozenEndDate(membershipEndDate: string, freezes: Array<{ duration_days: number }>): string {
  const frozenDays = freezes.reduce((sum, f) => sum + f.duration_days, 0);
  return addDaysToBaliKey(toBaliDateKey(membershipEndDate), -frozenDays);
}

/**
 * Validate freeze dates against membership period and existing freezes.
 * Returns an error message string, or null if valid.
 *
 * Mirrors the database rule in mutate_membership_freeze (migration
 * 20261102150000), which is the real boundary — this is only so the form can
 * say no before the request:
 *   - the freeze starts on or after the membership start date;
 *   - it starts on or before the UNFROZEN end (end_date minus every freeze's
 *     days, the one being edited included). Measured without the other freezes
 *     so that removing any one of them never leaves another starting after the
 *     membership has ended. The freeze may END after the membership's end date:
 *     the freeze itself pushes the end date out;
 *   - no overlap with another freeze. Dates are INCLUSIVE on both ends: a
 *     freeze ending on the 16th is still frozen on the 16th, so a new freeze may
 *     start no earlier than the 17th.
 */
export function validateFreezeDates(params: {
  startDate: string;
  endDate: string;
  membershipStartDate: string;
  membershipEndDate: string;
  existingFreezes: FreezeDates[];
  editingFreezeId?: string;
}): string | null {
  const { startDate, endDate, membershipStartDate, membershipEndDate, existingFreezes, editingFreezeId } = params;

  if (endDate < startDate) {
    return 'Freeze end date cannot be before start date';
  }

  if (startDate < toBaliDateKey(membershipStartDate)) {
    return 'Freeze start date cannot be before membership start date';
  }

  const unfrozenEnd = getUnfrozenEndDate(membershipEndDate, existingFreezes);
  if (startDate > unfrozenEnd) {
    return `Freeze must start on or before ${unfrozenEnd}, the last day of the membership without freezes`;
  }

  // No overlap with existing freezes (exclude the freeze being edited)
  for (const existing of existingFreezes) {
    if (editingFreezeId && existing.id === editingFreezeId) continue;
    if (startDate <= existing.end_date && endDate >= existing.start_date) {
      return `Overlaps with existing freeze (${existing.start_date} - ${existing.end_date})`;
    }
  }

  return null;
}

export type FreezePhase = 'upcoming' | 'in_progress' | 'ended';

/**
 * Where a freeze stands on the Bali calendar. A freeze starting today counts
 * as upcoming: nothing has been used yet, so deleting it costs the member no
 * frozen days. Past that, deleting takes back days already used, and a freeze
 * in progress is better ended today (planEndFreezeToday).
 */
export function getFreezePhase(freeze: { start_date: string; end_date: string }, todayKey: string): FreezePhase {
  if (todayKey <= freeze.start_date) return 'upcoming';
  if (todayKey <= freeze.end_date) return 'in_progress';
  return 'ended';
}

/**
 * "End freeze today" for a freeze in progress: the last frozen day becomes
 * yesterday, so the member can use the facility today and the membership end
 * date shrinks only by the days not yet used.
 */
export function planEndFreezeToday(
  freeze: { start_date: string; end_date: string; duration_days: number },
  membershipEndDate: string,
  todayKey: string,
): { newEndDate: string; newDurationDays: number; daysReturned: number; newMembershipEndDate: string } {
  const newEndDate = addDaysToBaliKey(todayKey, -1);
  const newDurationDays = diffBaliDays(freeze.start_date, newEndDate) + 1;
  const daysReturned = freeze.duration_days - newDurationDays;
  return {
    newEndDate,
    newDurationDays,
    daysReturned,
    newMembershipEndDate: addDaysToBaliKey(toBaliDateKey(membershipEndDate), -daysReturned),
  };
}
